import React from "react";
import {
  Ruler,
  Award,
  ShieldCheck,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { SITE } from "../site.config";

const STATS = [
  { value: "20+", label: "Years experience" },
  { value: "£0", label: "Free measure & quote" },
  { value: "15+", label: "Towns covered" },
  { value: "5★", label: "Google & Facebook" },
];

const FEATURES = [
  {
    icon: Ruler,
    title: "Free Measure & Quotation",
    body:
      "We come to you with the tape measure, talk you through the options and give you an honest, no-obligation written quote.",
    accent: "01",
  },
  {
    icon: Award,
    title: "Over 20 Years' Experience",
    body:
      "Two decades on the tools means a perfect finish around stairs, awkward thresholds and tricky subfloors — every time.",
    accent: "02",
  },
  {
    icon: ShieldCheck,
    title: "Fully Insured & Guaranteed",
    body:
      "All work is fully insured and quality-guaranteed. Carpets, LVT, vinyl, screeding — domestic, landlord and commercial.",
    accent: "03",
  },
];

const PILLS = [
  "Free Measure & Quote",
  "Fully Insured",
  "20+ Years",
  "Domestic & Commercial",
  "Landlord Specialists",
  "Quality Workmanship",
  "Competitive Prices",
  "Friendly Local Service",
];

export default function WhyChooseUs() {
  return (
    <section
      id="why"
      data-testid="why-section"
      className="bg-moffat-paper"
    >
      {/* Big stat strip */}
      <div className="bg-moffat-navy text-white relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(135deg, #ffffff 1px, transparent 1px), linear-gradient(45deg, #ffffff 1px, transparent 1px)",
            backgroundSize: "30px 30px",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-white/15">
            {STATS.map((s, idx) => (
              <div
                key={s.label}
                data-testid={`stat-${idx}`}
                className="py-5 md:py-2 px-2 md:px-6 text-center md:text-left first:pt-0 md:first:pl-0 last:pb-0 md:last:pr-0"
              >
                <p className="font-display text-5xl md:text-6xl lg:text-7xl text-white leading-none">
                  {s.value.includes("£") ? (
                    <>
                      <span className="text-moffat-red">£</span>
                      {s.value.slice(1)}
                    </>
                  ) : s.value.includes("+") ? (
                    <>
                      {s.value.replace("+", "")}
                      <span className="text-moffat-red">+</span>
                    </>
                  ) : s.value.includes("★") ? (
                    <>
                      {s.value.replace("★", "")}
                      <span className="text-yellow-400">★</span>
                    </>
                  ) : (
                    s.value
                  )}
                </p>
                <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] text-white/70 mt-2">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>
        {/* Bottom red strip */}
        <div className="h-1.5 bg-moffat-red" />
      </div>

      {/* Feature blocks */}
      <div className="section-pad">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="font-script text-3xl text-moffat-red leading-none">
              Why choose us
            </p>
            <h2 className="font-display text-4xl sm:text-5xl text-moffat-navy mt-3 uppercase leading-[0.95]">
              Local. Trusted.
              <br />
              <span className="brush-underline text-moffat-charcoal">Built to last.</span>
            </h2>
            <p className="mt-4 text-base text-moffat-muted leading-relaxed">
              Every job — big or small — gets the same attention to detail and
              the same honest service.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 md:gap-6">
            {FEATURES.map(({ icon: Icon, title, body, accent }) => (
              <article
                key={title}
                data-testid={`feature-${accent}`}
                className="group relative bg-white border border-moffat-line rounded-sm p-6 md:p-7 hover:shadow-2xl transition-all hover:-translate-y-1.5"
              >
                <p className="font-display text-7xl text-moffat-cream group-hover:text-moffat-red/20 transition-colors leading-none absolute top-4 right-5">
                  {accent}
                </p>
                <div className="relative h-12 w-12 rounded-sm bg-moffat-red text-white flex items-center justify-center">
                  <Icon size={22} strokeWidth={2.2} />
                </div>
                <h3 className="relative mt-5 font-display text-2xl text-moffat-navy uppercase leading-tight">
                  {title}
                </h3>
                <p className="relative mt-3 text-[15px] text-moffat-muted leading-relaxed">
                  {body}
                </p>
              </article>
            ))}
          </div>

          {/* Bottom pill bar */}
          <div className="mt-10 flex flex-wrap justify-center gap-2">
            {PILLS.map((p) => (
              <span
                key={p}
                className="inline-flex items-center gap-1.5 bg-white border border-moffat-line rounded-full px-3.5 py-1.5 text-[12px] font-bold text-moffat-navy"
              >
                <Sparkles size={11} strokeWidth={3} className="text-moffat-red" />
                {p}
              </span>
            ))}
          </div>

          {/* CTA band */}
          <div className="mt-12 bg-moffat-navy rounded-sm p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5 border-l-4 border-moffat-red">
            <div>
              <p className="font-display text-2xl md:text-3xl text-white uppercase leading-tight">
                Ready for your <span className="text-moffat-red">free quote?</span>
              </p>
              <p className="text-white/70 mt-2 text-sm md:text-base">
                Honest advice, competitive prices, no obligation. Call or
                WhatsApp anytime.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto md:flex-shrink-0">
              <a
                data-testid="why-quote-cta"
                href="#quote"
                className="inline-flex items-center justify-center gap-2 bg-moffat-red hover:bg-moffat-redDeep text-white font-bold text-sm md:text-base px-6 h-14 rounded-sm transition-all hover:-translate-y-0.5 whitespace-nowrap"
              >
                GET FREE QUOTE
                <ArrowRight size={16} strokeWidth={2.5} />
              </a>
              <a
                data-testid="why-call-cta"
                href={`tel:${SITE.phoneTel}`}
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-moffat-cream text-moffat-navy font-bold text-sm md:text-base px-6 h-14 rounded-sm transition-all hover:-translate-y-0.5 whitespace-nowrap"
              >
                CALL US TODAY
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
